import React, {useEffect, useState} from 'react';
import {View} from 'react-native';
import DropDownPicker, {ItemType} from 'react-native-dropdown-picker';
import {useDebouncedCallback} from 'use-debounce';

import {GeocodedSuburb} from '../../../models/middleware';
import apiService from '../../../transport/apiService';
import {suburbKey} from '../../../utils/address';

import styles from './styles';

type SuburbItemType = ItemType<string> & {suburb: GeocodedSuburb};

export const suburbToItem = (suburb: GeocodedSuburb): SuburbItemType => ({
  label: `${suburb.locality}, ${suburb.administrative_area} ${suburb.postal_code}`,
  // DropDownPicker uses `value` as the row key. It must be unique per suburb —
  // locality alone collides across states (ST AGNES QLD vs SA) and makes results
  // stack on each keystroke. See suburbKey + SuburbPicker.test.tsx.
  value: suburbKey(suburb),
  suburb,
});

type Props = {
  onSelectSuburb: (suburb: GeocodedSuburb) => void;
  initialSuburb?: {
    locality: string;
    administrative_area: string;
    postal_code: string;
  } | null;
  testID?: string;
};

const SuburbPicker = ({onSelectSuburb, initialSuburb, testID}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState<SuburbItemType[]>([]);
  const [value, setValue] = useState<string | null>(null);

  // Only initialize once on mount if initialSuburb has actual values
  useEffect(() => {
    if (initialSuburb?.locality) {
      const initialItem = suburbToItem(initialSuburb);
      setItems([initialItem]);
      setValue(initialItem.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const debouncedApiCall = useDebouncedCallback(async (text: string) => {
    const suburbs = await apiService.searchSuburbs(text);
    const suburbItems = suburbs.map(suburbToItem);
    setItems(suburbItems);
  }, 500);

  const handleSearchTextChange = (text: string) => {
    debouncedApiCall(text);
  };

  const handleChangeValue = (val: string | null) => {
    if (val) {
      const selectedItem = items.find(item => item.value === val);
      if (selectedItem) {
        onSelectSuburb(selectedItem.suburb);
      }
    }
  };

  return (
    <View>
      <DropDownPicker
        open={isOpen}
        value={value}
        items={items}
        setOpen={setIsOpen}
        searchable
        searchPlaceholder="Search for a suburb"
        placeholder="Select a suburb"
        setValue={setValue}
        setItems={setItems}
        onChangeSearchText={handleSearchTextChange}
        onChangeValue={handleChangeValue}
        listMode="SCROLLVIEW"
        disableLocalSearch={true}
        itemSeparator={true}
        showArrowIcon={false}
        disableBorderRadius={true}
        placeholderStyle={styles.placeholderStyle}
        style={[styles.pickerStyle, {display: isOpen ? 'none' : 'flex'}]}
        containerStyle={styles.containerStyle}
        labelStyle={styles.labelStyle}
        searchContainerStyle={styles.searchContainerStyle}
        searchTextInputStyle={[
          styles.searchTextInputStyle,
          isOpen ? styles.searchTextInputStyleOpen : null,
        ]}
        searchPlaceholderTextColor="#BBB"
        dropDownContainerStyle={styles.dropDownContainerStyle}
        listItemContainerStyle={styles.listItemContainerStyle}
        listItemLabelStyle={styles.listItemLabelStyle}
        itemSeparatorStyle={styles.itemSeparatorStyle}
        scrollViewProps={{keyboardShouldPersistTaps: 'always'}}
        testID={testID}
      />
    </View>
  );
};

export default SuburbPicker;
